import { Message } from "@cnx/common";
import {
  Box,
  Button,
  Group,
  Menu,
  Modal,
  Text,
  UnstyledButton,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconChevronLeft,
  IconDotsVertical,
  IconTrash,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "stores/authStore";

interface ChatSelectionHeaderProps {
  selectedMessages: Message[];
  onDeselect: () => void;
  onEdit: () => void;
  onCopy: () => void;
  onDelete: () => Promise<void>;
  isDeleting?: boolean;
}

export const ChatSelectionHeader = ({
  selectedMessages,
  onDeselect,
  onEdit,
  onCopy,
  onDelete,
  isDeleting = false,
}: ChatSelectionHeaderProps) => {
  const { t } = useTranslation("messages");
  const currentUserId = useAuthStore(state => state.currentUserId);
  const isSingleSelection = selectedMessages.length === 1;
  const selectedMessage = isSingleSelection ? selectedMessages[0] : null;
  const allOwn = selectedMessages.every(m => m.createdBy === currentUserId);
  const isOwn =
    !!selectedMessage && selectedMessage.createdBy === currentUserId;
  const [
    deleteModalOpened,
    { open: openDeleteModal, close: closeDeleteModal },
  ] = useDisclosure(false);

  return (
    <>
      <Modal
        opened={deleteModalOpened}
        onClose={closeDeleteModal}
        title={t("deleteConfirm", {
          count: selectedMessages.length,
        }).toUpperCase()}
        withCloseButton={false}
        centered
        zIndex={400}
        styles={{ title: { width: "100%", textAlign: "center" } }}
      >
        <Group justify="center">
          <Button color="gray" variant="outline" onClick={closeDeleteModal}>
            {t("cancel").toUpperCase()}
          </Button>
          <Button
            variant="filled"
            color="red"
            loading={isDeleting}
            miw={100}
            onClick={async () => {
              await onDelete();
              closeDeleteModal();
            }}
          >
            {t("delete").toUpperCase()}
          </Button>
        </Group>
      </Modal>

      <Box
        className="bg-white w-full flex items-center justify-between sticky top-0 z-10 px-2"
        mih={66}
        style={{ borderBottom: "1px solid var(--mantine-color-gray-2)" }}
      >
        <Box className="flex items-center gap-2">
          <UnstyledButton onClick={onDeselect} p={8} aria-label="Back">
            <IconChevronLeft size={24} color="var(--color-gray-dark)" />
          </UnstyledButton>
          <Text fw={600} fz="md">
            {t("selectedMessages", { count: selectedMessages.length })}
          </Text>
        </Box>

        <Box className="flex items-center">
          {allOwn && (
            <UnstyledButton
              p={8}
              onClick={openDeleteModal}
              aria-label={"Delete"}
              disabled={isDeleting}
            >
              <IconTrash size={24} />
            </UnstyledButton>
          )}

          {selectedMessages.length > 0 && (
            <Menu shadow="md" width={200} withArrow arrowOffset={0} offset={15}>
              <Menu.Target>
                <UnstyledButton p={8} aria-label="More options">
                  <IconDotsVertical size={24} />
                </UnstyledButton>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item onClick={onCopy} fw={400} fz="sm">
                  {t("copy")}
                </Menu.Item>
                {isOwn && (
                  <Menu.Item onClick={onEdit} fw={400} fz="sm">
                    {t("edit")}
                  </Menu.Item>
                )}
                {allOwn && (
                  <Menu.Item onClick={openDeleteModal} fw={400} fz="sm">
                    {t("delete")}
                  </Menu.Item>
                )}
              </Menu.Dropdown>
            </Menu>
          )}
        </Box>
      </Box>
    </>
  );
};
